/**
 * Photos and video an agent attaches to a reply, checked before anything is
 * sent so the agent can fix the file first.
 *
 * Two paths, chosen by ant's chatSupportStatus.mediaUploads:
 *   - on: photos and video go to the chat media bucket (../../hooks/chat/media)
 *     with the bucket's limits: PNG/JPEG up to 10 MB, MP4/MOV up to 50 MB
 *     and 60 seconds;
 *   - off: photos only, as a base64 PNG or JPEG of at most 2 MB once decoded
 *     (sendChatMessageAsAdmin.image), and no video.
 */

const MB = 1024 * 1024;

/** ant's legacy limit (Ant.Organizations.MerchantStore.Changes.UploadImage). */
export const MAX_IMAGE_BYTES = 2 * MB;

export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg'];

/** ant's bucket limits (Ant.Chat.Media). */
export const MAX_PHOTO_BYTES = 10 * MB;
export const MAX_VIDEO_BYTES = 50 * MB;
export const MAX_VIDEO_MS = 60_000;

const PHOTO_TYPES = ['image/jpeg', 'image/png'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];

/**
 * A container often reports a clip cut at 60 s as a little longer; up to this
 * much over is taken as 60 s (and sent as such, ant's maximum).
 */
const VIDEO_SLACK_MS = 500;

/** The poster ant takes: a JPEG of at most 1 MB. Frames are scaled to this side. */
const MAX_POSTER_BYTES = MB;
const POSTER_MAX_SIDE = 1280;

export type MediaKind = 'photo' | 'video';

/** What the file picker offers: photos and video with media uploads on, photos only off. */
export function acceptedTypes(mediaUploads: boolean): string[] {
  return mediaUploads ? [...PHOTO_TYPES, ...VIDEO_TYPES] : ACCEPTED_IMAGE_TYPES;
}

export function mediaKindOf(contentType: string): MediaKind | null {
  if (PHOTO_TYPES.includes(contentType)) return 'photo';
  if (VIDEO_TYPES.includes(contentType)) return 'video';
  return null;
}

const megabytes = (bytes: number) => (bytes / MB).toFixed(1);

/** The reason a file cannot go the legacy (base64) way, or null when it can. */
export function imageFileError(
  file: Pick<File, 'type' | 'size' | 'name'>
): string | null {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return `${file.name || 'This file'} is not a PNG or JPEG image.`;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `${file.name || 'This image'} is ${megabytes(
      file.size
    )} MB; photos can be at most 2 MB.`;
  }
  return null;
}

/**
 * The reason a file cannot be attached, or null when it can: the bucket's
 * types and limits with media uploads on, the legacy ones off. A video's
 * length is checked apart, once it has been read (videoLengthError).
 */
export function mediaFileError(
  file: Pick<File, 'type' | 'size' | 'name'>,
  mediaUploads: boolean
): string | null {
  if (!mediaUploads) return imageFileError(file);
  const name = file.name || 'This file';
  const kind = mediaKindOf(file.type);
  if (!kind) return `${name} is not a photo (PNG or JPEG) or a video (MP4 or MOV).`;
  if (kind === 'photo' && file.size > MAX_PHOTO_BYTES) {
    return `${name} is ${megabytes(file.size)} MB; photos can be at most 10 MB.`;
  }
  if (kind === 'video' && file.size > MAX_VIDEO_BYTES) {
    return `${name} is ${megabytes(file.size)} MB; videos can be at most 50 MB.`;
  }
  return null;
}

/** A length as m:ss. */
export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** The reason a video is too long, or null. */
export function videoLengthError(name: string, durationMs: number): string | null {
  if (durationMs <= MAX_VIDEO_MS + VIDEO_SLACK_MS) return null;
  return `${name || 'This video'} is ${formatDuration(
    durationMs
  )} long; videos can be at most 60 seconds.`;
}

/** The length ant is told: what was read, within ant's maximum. */
export const reportedDuration = (durationMs: number): number =>
  Math.min(durationMs, MAX_VIDEO_MS);

/** The file as a `data:image/...;base64,` URI, which ant accepts as is. */
export function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/** A photo's frame size, or null when the browser cannot tell (it is optional to ant). */
export function readImageSize(
  file: Blob,
  timeoutMs = 5000
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = document.createElement('img');
    let timer = 0;
    const done = (size: { width: number; height: number } | null) => {
      window.clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      URL.revokeObjectURL(url);
      resolve(size);
    };
    timer = window.setTimeout(() => done(null), timeoutMs);
    img.onload = () =>
      done(
        img.naturalWidth && img.naturalHeight
          ? { width: img.naturalWidth, height: img.naturalHeight }
          : null
      );
    img.onerror = () => done(null);
    img.src = url;
  });
}

export interface VideoProbe {
  durationMs: number;
  width: number | null;
  height: number | null;
  /** A JPEG still (at most 1 MB) for the rider's tile; null when none could be drawn. */
  poster: Blob | null;
}

/** The frame on screen as a JPEG within ant's poster limit, or null. */
function captureFrame(video: HTMLVideoElement): Promise<Blob | null> {
  const { videoWidth: width, videoHeight: height } = video;
  if (!width || !height) return Promise.resolve(null);
  const scale = Math.min(1, POSTER_MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext('2d');
  if (!context) return Promise.resolve(null);
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  const jpeg = (quality: number) =>
    new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  const fits = (blob: Blob | null) =>
    blob && blob.size <= MAX_POSTER_BYTES ? blob : null;
  return jpeg(0.8).then((first) => fits(first) ?? jpeg(0.6).then(fits));
}

/**
 * Opens the video in the browser for its length and frame size (a video
 * whose length cannot be read cannot be checked against 60 s, so it is
 * refused), then draws a frame a little past the start as its poster, best
 * effort: a codec the browser can list but not decode still gets through,
 * without a poster.
 */
export function probeVideo(
  file: Blob,
  { timeoutMs = 15_000, posterTimeoutMs = 5000 } = {}
): Promise<VideoProbe> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    let timer = 0;

    const finish = (settle: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      settle();
    };
    const fail = (why: string) => finish(() => reject(new Error(why)));

    timer = window.setTimeout(() => fail('the video did not load in time'), timeoutMs);
    video.onerror = () => fail('the browser cannot open this video');
    video.onloadedmetadata = () => {
      const seconds = video.duration;
      if (!Number.isFinite(seconds) || seconds <= 0) {
        fail('the video does not say how long it is');
        return;
      }
      const info = {
        durationMs: Math.round(seconds * 1000),
        width: video.videoWidth || null,
        height: video.videoHeight || null,
      };
      const withPoster = (poster: Blob | null) =>
        finish(() => resolve({ ...info, poster }));

      window.clearTimeout(timer);
      timer = window.setTimeout(() => withPoster(null), posterTimeoutMs);
      video.onerror = () => withPoster(null);
      video.onseeked = () => {
        captureFrame(video).then(withPoster, () => withPoster(null));
      };
      // Past a fade-in, but inside the shortest clip.
      video.currentTime = Math.min(0.5, seconds / 3);
    };

    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
  });
}
